/**
 * Extension UI registry — discovered (not yet activated) `ExtensionUI` modules.
 *
 * Mirrors `plugin-ui-registry` but for the third extension class. Entries are
 * keyed by `manifest.id`. The host activates entries on demand based on
 * `activationEvents` + enablement.
 */
import { resolveExtensionManifest, type ExtensionUI } from "@magic-md/extension-sdk";

const extensions = new Map<string, ExtensionUI>();
const listeners = new Set<() => void>();
let revision = 0;

function notify(): void {
    revision += 1;
    for (const fn of listeners) fn();
}

function localize(ext: ExtensionUI | undefined): ExtensionUI | undefined {
    if (!ext) return undefined;
    if (!ext.i18n) return ext;
    return { ...ext, manifest: resolveExtensionManifest(ext.manifest, ext.i18n) };
}

export function registerExtensionUI(ext: ExtensionUI): void {
    extensions.set(ext.manifest.id, ext);
    notify();
}

export function unregisterExtensionUI(id: string): boolean {
    const deleted = extensions.delete(id);
    if (deleted) notify();
    return deleted;
}

export const extensionUIRegistry = {
    /** Raw (un-localized) entry — used by the host activator. */
    getRaw(id: string): ExtensionUI | undefined {
        return extensions.get(id);
    },
    /** Localized entry — used for display. */
    get(id: string): ExtensionUI | undefined {
        return localize(extensions.get(id));
    },
    getAll(): Map<string, ExtensionUI> {
        return new Map(
            [...extensions.entries()].map(([id, ext]) => [id, localize(ext) ?? ext]),
        );
    },
    ids(): string[] {
        return [...extensions.keys()];
    },
    has(id: string): boolean {
        return extensions.has(id);
    },
    revision(): number {
        return revision;
    },
    get size(): number {
        return extensions.size;
    },
    subscribe(listener: () => void): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
    clear(): void {
        extensions.clear();
        notify();
    },
};
